import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Filter, ArrowUpAZ, ArrowDownAZ } from "lucide-react";
import { getAllOrderDetail } from "../../reducers/order.slice";
import {
  convertDateFormatInUtc,
  formatNumberToDollar,
  countActiveFiltersAndSort,
  subtractHoursUtc,
} from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
} from "../../components/ui/dropdown-menu";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import useMediaQuery from "../../components/shared/useMediaQuery";

const ORDER_STATUSES = ["PENDING_APPROVAL", "APPROVED", "REJECTED"];
const ORDER_TYPE_OPTIONS = [
  { value: "PRE_QUOTE", label: "Sell" },
  { value: "QUOTE", label: "Quote" },
  { value: "SHIP_DIRECT", label: "Ship Direct" },
];
const ORDER_TYPES = ORDER_TYPE_OPTIONS.map((option) => option.value);
const CHUNK_SIZE = 50;

const getStoredOrderTypes = () => {
  const stored = sessionStorage.getItem("openOrders_selectedTypes");
  if (!stored) return [];
  const parsed = JSON.parse(stored);
  return Array.isArray(parsed) ? parsed.filter((type) => ORDER_TYPES.includes(type)) : [];
};

const getOrderTypeLabel = (type) =>
  ORDER_TYPE_OPTIONS.find((option) => option.value === type)?.label ||
  type?.replace(/_/g, " ") ||
  "—";

const OpenOrderPage = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = sessionStorage.getItem("openOrders_selectedStatuses");
    return s ? JSON.parse(s) : [];
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    return getStoredOrderTypes();
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("openOrders_sortDirection") || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const s = sessionStorage.getItem("openOrders_rowsPerPage");
    return s ? JSON.parse(s) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());

  const isInitialRender = useRef(true);
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  const loadDataChunk = async (chunkNumber = 0) => {
    const filterKey = JSON.stringify({
      selectedStatuses: [...selectedStatuses].sort(),
      selectedTypes: [...selectedTypes].sort(),
      search: searchTerm,
      sortDirection,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;

    if (pageCache.has(cacheKey)) {
      const cached = pageCache.get(cacheKey);
      if (cached.totalRows !== undefined) setTotalRows(cached.totalRows);
      return cached.data;
    }
    if (loadingChunk) return [];

    setLoadingChunk(true);

    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
        order_status: selectedStatuses.length > 0 ? selectedStatuses : [],
        type: selectedTypes.length > 0 ? selectedTypes : [],
      };
      if (searchTerm?.trim()) payload.search = searchTerm.trim();
      if (sortDirection && sortDirection !== "null") {
        payload.sort_by = "ORDER_PRICE";
        payload.sort_order = sortDirection;
      }

      const res = await dispatch(getAllOrderDetail(payload));
      if (res?.error && Object.keys(res.error).length > 0) {
        showToast(res.error.message, "error");
        setHasMoreData(false);
        setFilteredData([]);
        return [];
      }
      if (!res?.payload?.data || !Array.isArray(res.payload.data)) {
        setHasMoreData(false);
        setFilteredData([]);
        return [];
      }

      if ((chunkNumber === 0 || !totalRows) && res.payload.total) setTotalRows(res.payload.total);

      const formatted = res.payload.data.map((item) => {
        const approvalBaseDate =
          item.status === "APPROVED"
            ? item.status_updated_at || subtractHoursUtc(item.order_expiry)
            : null;

        return {
          ...item,
          createdDate: convertDateFormatInUtc(item.created_at, "MMMM D, YYYY hh:mm:ss A"),
          approvalDate: approvalBaseDate
            ? convertDateFormatInUtc(approvalBaseDate, "MMM D, YYYY · h:mm A")
            : "—",
          orderPrice: formatNumberToDollar(item.order_price),
          newOrderItems: item.order_items?.map((oi) => ({
            ...oi,
            buy_price: formatNumberToDollar(oi.buy_price || 0),
            cashout_price: formatNumberToDollar(oi.cashout_price || 0),
            total_amount: formatNumberToDollar(oi.total_amount || 0),
          })),
        };
      });

      setPageCache((prev) => new Map(prev.set(cacheKey, { data: formatted, timestamp: Date.now(), totalRows: res.payload.total })));
      setHasMoreData(formatted.length === CHUNK_SIZE);
      return formatted;
    } catch (err) {
      showToast("Error fetching data", "error");
      setHasMoreData(false);
      return [];
    } finally {
      setLoadingChunk(false);
    }
  };

  const loadInitialData = async () => {
    const chunk = await loadDataChunk(0);
    setAllLoadedData(chunk || []);
  };

  const checkAndLoadMoreData = async (page, rpp) => {
    const required = (page + 1) * rpp;
    if (allLoadedData.length >= required || !hasMoreData || loadingChunk) return;
    if (totalRows > 0 && allLoadedData.length >= totalRows) { setHasMoreData(false); return; }
    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(allLoadedData.length / CHUNK_SIZE);
      const newData = await loadDataChunk(nextChunk);
      if (newData?.length > 0) {
        setAllLoadedData((prev) => [...prev, ...newData]);
        setTimeout(() => {
          const combined = [...allLoadedData, ...newData];
          setCurrentPage(page);
          setFilteredData(combined.slice(page * rpp, page * rpp + rpp));
        }, 100);
      } else {
        setHasMoreData(false);
      }
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  const handleChangePage = async (newPage) => {
    if (isNavigatingToPage || loadingChunk) return;
    try {
      setIsNavigatingToPage(true);
      const required = (newPage + 1) * rowsPerPage;
      if (allLoadedData.length < required && hasMoreData) {
        await checkAndLoadMoreData(newPage, rowsPerPage);
      } else {
        setCurrentPage(newPage);
        setFilteredData(allLoadedData.slice(newPage * rowsPerPage, newPage * rowsPerPage + rowsPerPage));
      }
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  const handleChangeRowsPerPage = (newRpp) => {
    setRowsPerPage(newRpp);
    sessionStorage.setItem("openOrders_rowsPerPage", JSON.stringify(newRpp));
    setCurrentPage(0);
    if (allLoadedData.length < newRpp && hasMoreData && !loadingChunk) {
      checkAndLoadMoreData(0, newRpp);
    }
  };

  const applyFilters = async () => {
    setLoadingChunk(true);
    setCurrentPage(0);
    setAllLoadedData([]);
    sessionStorage.setItem("openOrders_selectedStatuses", JSON.stringify(selectedStatuses));
    sessionStorage.setItem("openOrders_selectedTypes", JSON.stringify(selectedTypes));
    sessionStorage.setItem("openOrders_sortDirection", sortDirection);
    const results = await loadDataChunk(0);
    if (results?.length > 0) setAllLoadedData(results);
    else { setFilteredData([]); setAllLoadedData([]); }
    setLoadingChunk(false);
  };

  const handleRefresh = () => {
    setPageCache(new Map());
    setAllLoadedData([]);
    setHasMoreData(true);
    loadInitialData();
  };

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) { isInitialRender.current = false; return; }
    if (isSearching) return;
    const run = async () => {
      setIsSearching(true);
      setCurrentPage(0);
      setAllLoadedData([]);
      const results = await loadDataChunk(0);
      if (results?.length > 0) setAllLoadedData(results);
      else { setFilteredData([]); setAllLoadedData([]); }
      setIsSearching(false);
    };
    run();
  }, [debouncedSearch]);

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk || !allLoadedData?.length) return;
    const start = currentPage * rowsPerPage;
    const pageData = allLoadedData.slice(start, start + rowsPerPage);
    if (pageData.length > 0) setFilteredData(pageData);
    else if (currentPage > 0) setCurrentPage((p) => p - 1);
  }, [rowsPerPage, allLoadedData]);

  const columns = [
    { key: "id", label: "Order ID", width: 130 },
    { key: "orderPrice", label: "Order Price", width: 100, align: "right" },
    {
      key: "type",
      label: "Order Type",
      width: 120,
      render: (row) => getOrderTypeLabel(row.type),
    },
    { key: "created_by_name", label: "Member Name", width: 150 },
    { key: "createdDate", label: "Order Date", width: 190 },
    { key: "approvalDate", label: "Approval Date", width: 150 },
    {
      key: "status",
      label: "Status",
      width: 130,
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const itemsColumns = [
    { key: "id", label: "Item ID" },
    { key: "item_name", label: "Item Name" },
    { key: "upc", label: "UPC" },
    { key: "quote_id", label: "Quote ID" },
    { key: "quantity", label: "Quantity" },
    { key: "buy_price", label: "Buy Price" },
    { key: "cashout_price", label: "Cashout Price" },
    { key: "retailer_name", label: "Retailer Name" },
    { key: "total_amount", label: "Total Amount" },
    { key: "notes", label: "Notes" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const isMobile = useMediaQuery("(max-width: 640px)");
  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses, selectedTypes);

  const orderStats = useMemo(() => {
    const pending = allLoadedData.filter((d) => d.status === "PENDING_APPROVAL").length;
    const approved = allLoadedData.filter((d) => d.status === "APPROVED").length;
    const rejected = allLoadedData.filter((d) => d.status === "REJECTED").length;
    return { pending, approved, rejected };
  }, [allLoadedData]);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Open Orders"
          subtitle="Manage your ongoing orders and track progress."
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <div style={statStyle("#e8b84b")}>
          <span style={statLabelStyle}>PENDING APPROVAL</span>
          <span style={statValueStyle}>{orderStats.pending}</span>
        </div>
        <div style={statStyle("#4caf50")}>
          <span style={statLabelStyle}>APPROVED</span>
          <span style={statValueStyle}>{orderStats.approved}</span>
        </div>
        <div style={statStyle("#f27059")}>
          <span style={statLabelStyle}>REJECTED</span>
          <span style={statValueStyle}>{orderStats.rejected}</span>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: 10,
            flexWrap: "wrap",
            width: isMobile ? "100%" : "auto",
          }}
        >
          {!isMobile && <RefreshButton onRefresh={handleRefresh} />}
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setDebouncedTerm={setDebouncedSearch}
          />
          <DropdownMenu onOpenChange={(open) => { if (!open) applyFilters(); }}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                <Filter size={14} style={{ marginRight: 6 }} />
                Filter & Sort
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      background: "#00ADB5",
                      color: "#000",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "0 4px",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {ORDER_STATUSES.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={selectedStatuses.includes(status)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) =>
                    setSelectedStatuses((prev) =>
                      checked ? [...prev, status] : prev.filter((s) => s !== status)
                    )
                  }
                >
                  {status.replace(/_/g, " ")}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
              {ORDER_TYPE_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selectedTypes.includes(option.value)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) =>
                    setSelectedTypes((prev) =>
                      checked ? [...prev, option.value] : prev.filter((t) => t !== option.value)
                    )
                  }
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Sort by Price</DropdownMenuLabel>
              <DropdownMenuItem active={sortDirection === "ASC"} onClick={() => setSortDirection((prev) => prev === "ASC" ? null : "ASC")}>
                <ArrowUpAZ size={14} style={{ marginRight: 8 }} /> Low to High
              </DropdownMenuItem>
              <DropdownMenuItem active={sortDirection === "DESC"} onClick={() => setSortDirection((prev) => prev === "DESC" ? null : "DESC")}>
                <ArrowDownAZ size={14} style={{ marginRight: 8 }} /> High to Low
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        nestedColumns={itemsColumns}
        nestedRowKey="newOrderItems"
        nestedTableHeader="Order Items"
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => handleChangePage(n)}
        onRowsPerPageChange={(n) => handleChangeRowsPerPage(n)}
        loading={loadingChunk || isNavigatingToPage}
        minWidth={945}
      />
    </div>
  );
};

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

export default OpenOrderPage;
